import type { ReactElement } from "react";
import { useState } from "react";
import { translate } from "app/filters/i18nFilter";
import {
    TitleWidget,
    WidgetWrapper,
} from "app/pages/digital-suite/home-page/components/HomePageTrialNTLayout/DigitalSuiteTrialHomePageStyled";
import { SectionContainer, RecentsContainer, CardsList, ChevIcon, SeeMoreButton } from "./styledComponents";
import type { Recent } from "app/custom-hooks/useRecentsHook";
import { allTrackers } from "app/services/track/track";

const maxDisplay = 5;
const spaceBetweenCards = 8;

interface IRecentsWidgetProps {
    renderRecentCard: (recent: Recent | Partial<Recent>) => ReactElement;
    relevantRecents: Recent[] | Partial<Recent>[];
    cardHeight: number;
    title?: string;
    compactDisplay?: number;
}

export const RecentsWidget = (props: IRecentsWidgetProps) => {
    const {
        renderRecentCard,
        relevantRecents,
        cardHeight,
        title = "digitalmarketing.homepage.recents_widget.title",
        compactDisplay = 3,
    } = props;
    const [fullDisplay, setFullDisplay] = useState<boolean>(false);
    const canShowMore = relevantRecents.length > compactDisplay;

    const onShowMoreClick = (): void => {
        allTrackers.dispatchTrackEvent(
            "Button",
            "click",
            `Recent Queries/Show more/${fullDisplay ? "less" : "more"} recent pages`,
        );
        setFullDisplay((prev) => !prev);
    };

    return (
        <WidgetWrapper
            displayed={!!relevantRecents.length}
            data-automation="platform-homepage-recents"
        >
            <SectionContainer>
                <TitleWidget>{translate(title)}</TitleWidget>
                <RecentsContainer>
                    <CardsList
                        open={fullDisplay}
                        cardHeight={cardHeight}
                        maxDisplay={maxDisplay}
                        spaceBetween={spaceBetweenCards}
                        compactDisplay={Math.min(compactDisplay, relevantRecents.length)}
                    >
                        {relevantRecents?.map((recent) => renderRecentCard(recent)).slice(0, maxDisplay)}
                    </CardsList>
                    {canShowMore ? (
                        <div data-automation={"expansion-button"}>
                            <SeeMoreButton onClick={onShowMoreClick}>
                                <ChevIcon
                                    iconName={"chev-down"}
                                    directionDown={!fullDisplay}
                                    size="xs"
                                />
                                {translate(
                                    fullDisplay
                                        ? "digitalmarketing.homepage.recents_widget.see_less"
                                        : "digitalmarketing.homepage.recents_widget.see_more",
                                )}
                            </SeeMoreButton>
                        </div>
                    ) : null}
                </RecentsContainer>
            </SectionContainer>
        </WidgetWrapper>
    );
};
